import {NgModule} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {RouterModule} from '@angular/router';
import {TranslateModule} from '@ngx-translate/core';
import {NgxsFormPluginModule} from '@ngxs/form-plugin';
import {NgxMaskModule} from 'ngx-mask';
import {AddressFormComponent} from './components/address-form';
import {AddressFullPipe} from './pipes/address-full.pipe';
import {AddressShortPipe} from './pipes/address-short.pipe';
import {CardBodyComponent} from './components/card/components/card-body';
import {CardComponent} from './components/card';
import {CardFooterComponent} from './components/card/components/card-footer';
import {CardHeaderComponent} from './components/card/components/card-header';
import {CheckboxComponent} from './components/checkbox';
import {CheckboxGroupComponent} from './components/checkbox-group';
import {CustomNgZorroModule} from './modules/custom-ng-zorro.module';
import {DashboardComponent} from './components/dashboard';
import {DatatableComponent} from './components/datatable';
import {DatepickerComponent} from './components/datepicker';
import {EmptyValuePipe} from './pipes/empty-value.pipe';
import {FormControlErrorsComponent} from './components/form-control-errors';
import {FormFieldComponent} from './components/form-field';
import {FormFieldRowComponent} from './components/form-field-row';
import {FormGroupComponent} from './components/form-group';
import {IconBooleanComponent} from './components/icon-boolean';
import {IconButtonComponent} from './components/icon-button';
import {IconComponent} from './components/icon';
import {LsnDatePipe} from './pipes/lsn-date.pipe';
import {NavTileComponent} from './components/nav-tile';
import {SelectComponent} from './components/select';
import {TextInputComponent} from './components/text-input';
import {NumericInputComponent} from './components/numeric-input';
import {NavButtonComponent} from './components/nav-button';
import {WidthDirective} from './directives/width.directive';
import {TextareaComponent} from './components/textarea';
import {UploaderComponent} from './components/uploader';
import {HumanSizePipe} from './pipes/human-size.pipe';
import {ScrollingModule} from '@angular/cdk/scrolling';
import {ClipboardModule} from '@angular/cdk/clipboard';
import {LoaderComponent} from './components/loader/loader.component';
import {LsnDateTimePipe} from './pipes/lsn-datetime.pipe';
import {NavLinkComponent} from './components/nav-link';
import {DocumentListComponent} from './pages/document-list';
import {DocumentListFiltersComponent} from './pages/document-list/components/document-list-filters';
import {LsnCurrencyPipe} from './pipes/currency.pipe';
import {BooleanPipe} from './pipes/boolean.pipe';
import {AutocompleteTextInputComponent} from './components/autocomplete-text-input';
import {ClauseRadioComponent} from './components/clause-radio';
import {LogoComponent} from './components/logo/logo.component';
import {CollectionLabelComponent} from './components/collection-label';
import {TextOutputComponent} from './components/text-output/text-output.component';
import {VariantRadioGroupComponent} from './components/variant-radio-group';
import {VariantRadioComponent} from './components/variant-radio-group/component/variant-radio.component';
import {FormErrorsAlertComponent} from './components/form-errors-alert/form-errors-alert.component';
import {
    AsideBodyDirective,
    AsideLeftButtonsDirective,
    AsideRightButtonsDirective,
    AsideTemplateComponent
} from './components/aside-template/aside-template.component';
import {AclCheckDirective} from './directives/acl-check.directive';
import {LogoProjComponent} from './components/logo-proj/logo-proj.component';
import {PasswordPolicyInfoComponent} from './components/password-policy-info/password-policy-info.component';
import {LoaderSpinComponent} from './components/loader/components/loader-spin.component';
import {ClausesOutputComponent} from './components/clauses-output/clauses-output.component';
import {AutocompleteModelComponent} from './components/autocomplete-model/autocomplete-model.component';
import {AutocompleteBrandComponent} from './components/autocomplete-brand/autocomplete-brand.component';
import {AutocompleteTranscoderIndexComponent} from './components/autocomplete-transcoder-index/autocomplete-transcoder-index.component';
import {PasswordChangeFormComponent} from './components/password-change-form/password-change-form.component';
import {FullNamePipe} from './pipes/full-name.pipe';
import {BreadcrumbsComponent} from './components/breadcrumbs';
import {ContentComponent} from './components/content';
import {UploadFileErrorsComponent} from './components/upload-file-errors';
import {ConfirmationModalComponent} from './components/confirmation-modal';
import {CpiPartyFormComponent} from './components/cpi-party-form';
import {FormControlPipe} from './pipes/form-control.pipe';
import {OfferQuestionnaireComponent} from './components/offer-questionnaire';
import {DestroyerComponent} from './components/destroyer/destroyer.component';
import {RoleCheckDirective} from './directives/role-check.directive';
import {LsnNumericModule} from '../lsn-numeric/numeric.module';
import {BankAccountPipe} from './pipes/bank-account.pipe';

const customComponents = [
    AddressFormComponent,
    AsideTemplateComponent,
    AutocompleteBrandComponent,
    AutocompleteModelComponent,
    AutocompleteTextInputComponent,
    AutocompleteTranscoderIndexComponent,
    BreadcrumbsComponent,
    CardBodyComponent,
    CardComponent,
    CardFooterComponent,
    CardHeaderComponent,
    CheckboxComponent,
    CheckboxGroupComponent,
    ClauseRadioComponent,
    ClausesOutputComponent,
    CollectionLabelComponent,
    ConfirmationModalComponent,
    ContentComponent,
    CpiPartyFormComponent,
    DashboardComponent,
    DatatableComponent,
    DatepickerComponent,
    DestroyerComponent,
    DocumentListComponent,
    DocumentListFiltersComponent,
    FormControlErrorsComponent,
    FormFieldComponent,
    FormFieldRowComponent,
    FormGroupComponent,
    FormErrorsAlertComponent,
    IconBooleanComponent,
    IconButtonComponent,
    IconComponent,
    LoaderComponent,
    LoaderSpinComponent,
    LogoComponent,
    LogoProjComponent,
    NavButtonComponent,
    NavLinkComponent,
    NavTileComponent,
    NumericInputComponent,
    PasswordChangeFormComponent,
    PasswordPolicyInfoComponent,
    SelectComponent,
    TextareaComponent,
    TextInputComponent,
    TextOutputComponent,
    UploaderComponent,
    UploadFileErrorsComponent,
    VariantRadioComponent,
    VariantRadioGroupComponent,
    OfferQuestionnaireComponent,
];

const customDirectives = [
    WidthDirective,
    AsideBodyDirective,
    AsideLeftButtonsDirective,
    AsideRightButtonsDirective,
    AclCheckDirective,
    RoleCheckDirective
];

const customPipes = [
    AddressFullPipe,
    AddressShortPipe,
    BankAccountPipe,
    BooleanPipe,
    EmptyValuePipe,
    HumanSizePipe,
    LsnDatePipe,
    LsnDateTimePipe,
    LsnCurrencyPipe,
    FullNamePipe,
    FormControlPipe,
];

const importedModules = [
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    NgxsFormPluginModule,
    CustomNgZorroModule,
    ClipboardModule,
    ScrollingModule,
];

const importedModulesWithProviders = [
    LsnNumericModule.forRoot({
        default: {
            decimals: '.',
            precision: 2,
            min: 0
        },
        custom: {
            value100: {
                max: 99.99
            },
            value10k: {
                max: 9999.99
            },
            value100k: {
                max: 99999.99
            },
            percentage: {
                precision: 0,
                min: 0,
                max: 100
            },
        }
    })
];

@NgModule({
    declarations: [
        ...customComponents,
        ...customDirectives,
        ...customPipes,
    ],
    imports: [
        CommonModule,
        RouterModule,
        ...importedModules,
        ...importedModulesWithProviders,
        NgxMaskModule.forChild()
    ],
    providers: [],
    exports: [
        ...importedModules,
        ...customComponents,
        ...customDirectives,
        ...customPipes,

    ],
})

export class SharedModule {
}
